import React from "react";
import { Box, Stack } from "@mui/material";

/**
 * A card header in three columns: what the card is about on the left, the
 * controls that change it in the middle, and a legend or secondary control on
 * the right.
 *
 * The flanks are `flex: 1 1 0`, so they take equal width whatever they hold.
 * That is what puts the controls in the middle of the *card*; a plain
 * space-between row only centres them between their two neighbours, which
 * drifts as the heading or the legend changes width.
 *
 * Below md there is no room for three columns, so each part takes a full row
 * of its own and centres — the controls stay under the heading they belong to
 * instead of being squeezed against it.
 */
const CardHeaderRow: React.FC<{
  title: React.ReactNode;
  /** The switches this card is driven by. Centred on the card. */
  controls: React.ReactNode;
  /** Legend, or a control that is not a restatement of the same data. */
  aside?: React.ReactNode;
  /** Space below the header; cards differ slightly. */
  mb?: number;
  /**
   * Keep the heading on one line. The title then takes the width it needs
   * rather than an equal share, and the controls wrap to the next row when a
   * narrow card has no room — for half-width cards, where an equal flank would
   * fold a heading onto three lines.
   */
  titleNoWrap?: boolean;
}> = ({ title, controls, aside, mb = 1, titleNoWrap = false }) => (
  <Stack direction="row" alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb }}>
    <Box
      sx={
        titleNoWrap
          ? { flex: "1 0 auto", whiteSpace: "nowrap" }
          : { flex: { xs: "1 1 100%", md: "1 1 0" }, minWidth: 0 }
      }
    >
      {title}
    </Box>

    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      rowGap={1}
      sx={{ flex: { xs: "1 1 100%", md: "0 0 auto" } }}
    >
      {controls}
    </Stack>

    <Box
      sx={{
        flex: { xs: "1 1 100%", md: "1 1 0" },
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "center", md: "flex-end" },
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {aside}
    </Box>
  </Stack>
);

export default CardHeaderRow;
